import { getColor } from 'common/constants/_colors';
import { SPACES } from 'common/constants/sizes';
import Text from 'components/base/Text';
import { Flex, Margin } from 'components/base/layout-components';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSamlSensorAuthorization } from 'state/selectors/sensors-authorizations';
import styled from 'styled-components';

import {
  ACCOUNT_CREATION_SOURCE,
  ACCOUNT_TYPES_NAMES,
  FEATURE_FLAGS_NAMES,
  SENSOR_STATES
} from '@linearb/linjs-common';
import Link from '@material-ui/core/Link';
import AppRouter from 'common/AppRouter';
import { DATE_FORMAT } from 'common/constants/constants';
import { navigateBookDemoClick } from 'common/utils/utils';
import SearchInput from 'components/SearchInput';
import useDebounce from 'components/hooks/useDebounce';
import useSettingsService from 'components/hooks/useSettingsService';
import { createUserInviteModalWithViewerOptions } from 'modals/modal-options-factory';
import { useHistory } from 'react-router';
import UsersActions from './components/UsersActions';
import UsersTable from './components/UsersTable';
import UsersTitle from './components/UsersTitle';
import { teamsSelector } from 'state/selectors/teams';
import { accountScopesToTeamsColumnValues, getTeamScopes } from './users.utils';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from 'components/base/Icon';
import BulkAccountsCheckbox from './components/BulkAccountsCheckbox';
import { capitalize, upperCase } from 'lodash';
import ScimStatusBar from '../../components/SettingStatusBar/status-bars/ScimStatusBar';
import useScimSetting from 'common/hooks/settings/useScimSetting';
import PermissionButton from './permissions/PermissionButton';
import deburr from 'lodash/deburr';

const InviteMoreBox = styled(Flex)`
  background-color: ${getColor('feta')};
  padding: 0.5rem 1rem;
  margin-left: 1rem;
`;

const Users = ({
  accounts,
  myAccount,
  license,
  isTeamLicense,
  getAccounts,
  toggleModal,
  sendAccountInvitation,
  toggleSnackBar,
  getInvitations,
  destroyInvitation,
  destroyAccount,
  resendInvitation,
  changeInvitationAccountType,
  changeUserAccountType
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const [visibleAccounts, setVisibleAccounts] = useState(accounts);
  const [searchInput, setSearchInput] = useState('');
  const [accountsBulkSelected, setAccountsBulkSelected] = useState([]);
  const teams = useSelector(teamsSelector);
  const history = useHistory();
  const scimSetting = useScimSetting();

  useDebounce(
    () => {
      setVisibleAccounts(
        accounts.filter(
          ({ name = '', email = '' }) =>
            deburr(name).toLowerCase().includes(deburr(searchInput)) ||
            email.toLowerCase().includes(searchInput)
        )
      );
    },
    [accounts, searchInput],
    300
  );

  const onInviteUser = () =>
    history.push({
      pathname: `${AppRouter.USERS_INVITE_USER.path}`,
      search: 'isOrgSettings=true'
    });

  useEffect(() => {
    getAccounts();
    getInvitations();
  }, []);

  const forceSSOLoginFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.forceSSOLogin
  });
  const isRBACEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isRBACEnabled
  });
  const isFreePlusFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.showDoraDashboardV2
  });

  const isRBACEnabled = isRBACEnabledFF && !isTeamLicense;
  const samlSensorAuthorization = useSelector(getSamlSensorAuthorization);
  const forceSSO =
    forceSSOLoginFF && samlSensorAuthorization?.profile?.forceSSO;
  const ssoOK = samlSensorAuthorization?.state === SENSOR_STATES.ok;
  const ssoForceAndOk = forceSSO && ssoOK;

  const openUserInviteModal = () =>
    toggleModal(
      createUserInviteModalWithViewerOptions({
        sendAccountInvitation,
        toggleSnackBar,
        onSubmit: getInvitations,
        myAccount
      })
    );

  const onEditBulkPermissions = () =>
    history.push({
      pathname: `${AppRouter.USERS_PERMISSIONS_MULTIPLE.path}`,
      search: 'isOrgSettings=true',
      state: {
        accountIds: accountsBulkSelected
      }
    });

  const getSourceText = source => {
    if (!source) return '---';

    const upperCaseSources = [
      ACCOUNT_CREATION_SOURCE.SCIM,
      ACCOUNT_CREATION_SOURCE.SSO
    ];
    return upperCaseSources.includes(source)
      ? upperCase(source)
      : capitalize(source);
  };

  const accountsWithRelevantFields = useMemo(
    () =>
      visibleAccounts.map(account => {
        const {
          id,
          name,
          invitee_name,
          email,
          extra,
          isPending,
          isExpired = false,
          confirmed_at,
          invited_at,
          type_id,
          account_scopes: accountScopes
        } = account;
        const activationText = isPending ? 'Pending' : '---';
        const color = isPending ? 'gray' : 'black';
        const displayName = invitee_name || name;
        const source = getSourceText(extra?.source);

        const textParams = {
          size: 'large',
          color: isExpired ? getColor('torchRed') : color,
          italic: isPending
        };
        const teamsColumnValues = isRBACEnabled
          ? accountScopesToTeamsColumnValues({ accountScopes, teams })
          : null;

        const showWarning =
          !accountScopes || !getTeamScopes(accountScopes).length;
        const showBulkSelection = isRBACEnabled && myAccount?.id !== id;

        return {
          ...(showBulkSelection
            ? {
                bulkSelection: (
                  <BulkAccountsCheckbox
                    account={account}
                    accountsBulkSelected={accountsBulkSelected}
                    setAccountsBulkSelected={setAccountsBulkSelected}
                    isScimEnabled={scimSetting?.isEnabled}
                  />
                )
              }
            : []),
          name: (
            <Text {...textParams} ellipsisAfter='40rem'>
              {displayName}
            </Text>
          ),
          email: (
            <Text {...textParams} ellipsisAfter='40rem'>
              {email}
            </Text>
          ),
          source: <Text {...textParams}>{source}</Text>,
          role: <Text {...textParams}>{ACCOUNT_TYPES_NAMES[type_id]}</Text>,
          warning:
            isRBACEnabled && showWarning ? (
              <Icon name='warning' color='orangePeel' />
            ) : undefined,
          teams: isRBACEnabled ? (
            <Tooltip
              disableHoverListener={!teamsColumnValues.tooltip}
              title={teamsColumnValues.tooltip}
            >
              <Text {...textParams}>{teamsColumnValues.name}</Text>
            </Tooltip>
          ) : undefined,
          invitation_date: (
            <Text {...textParams}>
              {invited_at ? moment(invited_at).format(DATE_FORMAT) : '---'}
            </Text>
          ),
          activation_date: confirmed_at ? (
            <Text {...textParams}>
              {moment(confirmed_at).format(DATE_FORMAT)}
            </Text>
          ) : (
            <Text {...textParams}>
              {' '}
              {isExpired ? 'Expired' : activationText}
            </Text>
          ),
          actions: (
            <UsersActions
              resendInvitation={resendInvitation}
              myAccount={myAccount}
              account={account}
              toggleSnackBar={toggleSnackBar}
              destroyInvitation={destroyInvitation}
              destroyAccount={destroyAccount}
              toggleModal={toggleModal}
              changeUserAccountType={changeUserAccountType}
              changeInvitationAccountType={changeInvitationAccountType}
              ssoForceAndOk={ssoForceAndOk}
              isRBACEnabled={isRBACEnabled}
            />
          )
        };
      }),
    [visibleAccounts]
  );
  const { maxUsers } = license;
  const canInviteAmount = maxUsers - accounts.length;
  const hasReachLimitOfInvites = maxUsers && canInviteAmount === 0;
  const showInviteMore = !!maxUsers && ssoForceAndOk && !!canInviteAmount;
  const showMaxUsersMessage = !isFreePlusFF && isTeamLicense && !!maxUsers;
  return (
    <Flex direction='column'>
      {scimSetting.isEnabled && <ScimStatusBar withCTA />}
      <Flex justify='space-between' items='center' mb='small'>
        <Flex direction='column'>
          <Flex items='center'>
            <UsersTitle accounts={visibleAccounts} />
            {showInviteMore && !isRBACEnabledFF && (
              <InviteMoreBox onClick={openUserInviteModal}>
                <Text cursor='pointer' color='funGreen'>
                  invite {canInviteAmount} more!
                </Text>
              </InviteMoreBox>
            )}
          </Flex>
          {showMaxUsersMessage && (
            <Flex direction='column' mt='small'>
              <Text pb={SPACES.tiny}>
                You are on the free plan, and can invite up to {maxUsers}{' '}
                people.
              </Text>
              <Flex items='baseline'>
                <Text>To invite more people, and create more teams,</Text>
                &nbsp;
                <Link onClick={() => navigateBookDemoClick()}>
                  upgrade your plan.
                </Link>
              </Flex>
            </Flex>
          )}
        </Flex>
        <Flex>
          {isRBACEnabled && (
            <Margin r='normal'>
              <PermissionButton
                disabled={!accountsBulkSelected.length}
                variant='outline'
                size='lg'
                onClick={() => onEditBulkPermissions()}
              >
                Edit Permissions
              </PermissionButton>
            </Margin>
          )}
          {!ssoForceAndOk && (
            <PermissionButton
              size='lg'
              onClick={() =>
                hasReachLimitOfInvites
                  ? navigateBookDemoClick()
                  : isRBACEnabledFF
                  ? onInviteUser()
                  : openUserInviteModal()
              }
              dataCy='invite-user-button'
            >
              {hasReachLimitOfInvites
                ? 'Upgrade to invite more users'
                : 'Invite Users'}
            </PermissionButton>
          )}
        </Flex>
      </Flex>
      {!isTeamLicense && (
        <Flex height='3rem' width='20%' mb='medium'>
          <SearchInput
            placeholder='Search for Users'
            value={searchInput}
            onChange={val => setSearchInput(val.toLowerCase())}
          />
        </Flex>
      )}
      <UsersTable
        data={accountsWithRelevantFields}
        isRBACEnabled={isRBACEnabled}
      />
    </Flex>
  );
};

export default Users;
